import { Check, ArrowUpRight } from "lucide-react";
import { Reveal } from "./Reveal";

const cards = [
  {
    badge: "UAE / India / Global · Fractional CFO",
    title: "Fractional CFO",
    description:
      "Setting up scale-ready financial operations for ventures, alongside investor readiness and strategic planning across hiring, pricing, market expansion, and fundraising.",
    items: [
      "Forecasting & budgeting",
      "Cash flow management",
      "MIS & reporting",
      "Fundraise prep",
    ],
    accent: "dark",
  },
  {
    badge: "India focused · Venture Incubation",
    title: "Venture Incubation & Investor Readiness",
    description:
      "Partnering with deeptech and technical founders to turn technical breakthroughs into commercially viable, investor-backable ventures.",
    items: [
      "Market positioning",
      "Business model design",
      "Investor materials",
      "Early operating plan",
    ],
    accent: "light",
  },
  {
    badge: "India / US focused · Angel Investments",
    title: "Angel Capital",
    description:
      "Selective early cheques into ambitious founders where we can add value beyond capital.",
    items: [
      "Inception capital",
      "Deeptech & B2B focus",
      "Hands-on support",
      "Investor access",
    ],
    accent: "green",
  },
];

const WhatIDo = () => {
  return (
    <section
      id="offerings"
      data-testid="what-i-do-section"
      className="relative py-24 md:py-32 border-t border-ink/15"
    >
      <div className="mx-auto max-w-[1400px] px-5 md:px-8 lg:px-12">
        <Reveal direction="up">
          <div className="flex items-center gap-3 mb-4">
              <span className="font-mono text-[11px] uppercase tracking-[0.22em] text-ink/60">
                What we do
              </span>
          </div>
        </Reveal>

        <Reveal direction="up" delay={0.1}>
          <h2 className="font-display font-black uppercase text-[clamp(2rem,5vw,4rem)] leading-[0.95] tracking-[-0.02em] text-ink max-w-[20ch]">
            Ways to{" "}
            <span className="italic font-medium normal-case text-money">work together</span>
          </h2>
        </Reveal>

        <div className="mt-14 md:mt-20 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-7">
          {cards.map((c, idx) => {
            const dark = c.accent === "dark";
            const green = c.accent === "green";
            const baseBg = dark ? "bg-ink text-cream" : green ? "bg-white text-ink" : "bg-white text-ink";
            const badgeStyle = dark
              ? "bg-acid text-ink border-acid"
              : green
              ? "bg-money text-cream border-money"
              : "bg-cream text-ink border-ink";
            const checkColor = dark
              ? "text-acid"
              : green
              ? "text-money"
              : "text-ink";
            const subColor = dark ? "text-cream/75" : "text-ink/70";
            const liColor = dark ? "text-cream/90" : "text-ink/85";

            return (
              <Reveal
                key={c.title}
                direction="up"
                delay={0.15 + idx * 0.1}
                duration={0.8}
              >
                <div
                  data-testid={`offering-card-${idx}`}
                  className={`group relative h-full flex flex-col p-8 md:p-9 border border-ink transition-all duration-500 ${baseBg} hover:-translate-y-1 hover:brutalist-shadow`}
                >
                  <div className="flex items-start justify-between gap-3 mb-7">
                    <span
                      className={`inline-flex items-center gap-2 px-2.5 py-1 font-mono text-[10px] leading-snug uppercase tracking-[0.12em] border max-w-[80%] ${badgeStyle}`}
                    >
                      <span className={`h-1.5 w-1.5 shrink-0 rounded-full ${dark ? "bg-ink" : green ? "bg-cream" : "bg-ink"}`} />
                      <span className="break-words">{c.badge}</span>
                    </span>
                    <ArrowUpRight
                      className={`w-6 h-6 shrink-0 transition-all duration-500 group-hover:rotate-45 ${
                        dark ? "text-acid" : green ? "text-money" : "text-ink"
                      }`}
                    />
                  </div>

                  <h3 className="font-display font-black uppercase text-2xl md:text-[28px] lg:text-3xl tracking-[-0.02em] leading-[0.98] mb-5">
                    {c.title}
                  </h3>

                  <p className={`font-sans-pro text-[15px] md:text-base leading-relaxed mb-7 ${subColor}`}>
                    {c.description}
                  </p>

                  <ul className="space-y-3 mt-auto pt-6 border-t border-current/20">
                    {c.items.map((it, i) => (
                      <li
                        key={i}
                        className={`flex items-start gap-3 font-sans-pro text-sm md:text-[15px] ${liColor}`}
                      >
                        <Check className={`w-4 h-4 mt-1 shrink-0 ${checkColor}`} />
                        <span>{it}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </Reveal>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export default WhatIDo;
